import { cn } from '@/lib/utils';
import { getStatusDescription, getStatusColor, getCouponStatusInfo } from '../utils/statusCodes';
import type { CouponStatusType } from '../utils/types';

interface BookingStatusBadgeProps {
  statusCode: string;
  showDot?: boolean;
  className?: string;
}

/**
 * Badge for booking status codes (HK, HL, etc.)
 */
export function BookingStatusBadge({ statusCode, showDot = true, className }: BookingStatusBadgeProps) {
  const description = getStatusDescription(statusCode);
  const color = getStatusColor(statusCode);

  const colorClasses = {
    green: 'text-green-700',
    amber: 'text-amber-700',
    slate: 'text-slate-600',
  };

  const dotClasses = {
    green: 'bg-green-500',
    amber: 'bg-amber-500',
    slate: 'bg-slate-400',
  };

  return (
    <span className={cn('inline-flex items-center gap-1.5 text-sm font-medium', colorClasses[color], className)}>
      {showDot && (
        <span className={cn('w-2 h-2 rounded-full', dotClasses[color])} />
      )}
      {description}
    </span>
  );
}

interface CouponStatusBadgeProps {
  status: CouponStatusType;
  className?: string;
}

/**
 * Badge for coupon/ticket status (OPEN, FLOWN, etc.)
 */
export function CouponStatusBadge({ status, className }: CouponStatusBadgeProps) {
  const { label, variant } = getCouponStatusInfo(status);

  const variantClasses = {
    success: 'bg-green-100 text-green-700 border-green-200',
    warning: 'bg-amber-100 text-amber-700 border-amber-200',
    secondary: 'bg-slate-100 text-slate-600 border-slate-200',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border',
        variantClasses[variant],
        className
      )}
    >
      {label}
    </span>
  );
}

interface BookingClassBadgeProps {
  bookingClass: string;
  className?: string;
}

/**
 * Large badge for booking class (Y, J, F, etc.)
 */
export function BookingClassBadge({ bookingClass, className }: BookingClassBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center w-10 h-10 rounded-lg',
        'bg-slate-100 text-slate-800 text-xl font-bold',
        className
      )}
    >
      {bookingClass}
    </span>
  );
}

interface SSRCodeBadgeProps {
  code: string;
  className?: string;
}

/**
 * Badge for SSR codes (DOCS, CTCE, etc.)
 */
export function SSRCodeBadge({ code, className }: SSRCodeBadgeProps) {
  const isWarning = code === 'OTHS';

  return (
    <span
      className={cn(
        'inline-flex items-center px-1.5 py-0.5 rounded font-mono text-xs font-semibold',
        isWarning
          ? 'bg-orange-100 text-orange-700'
          : 'bg-slate-100 text-slate-600',
        className
      )}
    >
      {code}
    </span>
  );
}

interface TaxCodeBadgeProps {
  code: string;
  className?: string;
}

/**
 * Badge for tax codes (RA, DE, etc.)
 */
export function TaxCodeBadge({ code, className }: TaxCodeBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center px-1.5 py-0.5 rounded font-mono text-xs font-medium',
        'bg-slate-100 text-slate-600',
        className
      )}
    >
      {code}
    </span>
  );
}
